import type { ReactElement } from 'react'

import { useParentScreenshotGallery } from '@/components/features/use-parent-screenshot-gallery'
import { useParentScreenshotViewer } from '@/components/features/use-parent-screenshot-viewer'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'

export function ParentScreenshotGallery({ deviceId }: { deviceId: string }): ReactElement | null {
  const { query, screenshots } = useParentScreenshotGallery(deviceId)
  const viewer = useParentScreenshotViewer()

  if (query.isSuccess && screenshots.length === 0) {
    return null
  }

  return (
    <section aria-labelledby="screenshot-gallery-heading" className="space-y-3">
      <h2 id="screenshot-gallery-heading" className="text-lg font-semibold">
        Screenshot gallery
      </h2>
      {query.isPending ? (
        <div role="status" aria-label="Loading screenshot gallery" className="grid grid-cols-3 gap-2">
          {Array.from({ length: 6 }, (_, tile) => (
            <Skeleton key={tile} className="aspect-square w-full rounded-md" />
          ))}
        </div>
      ) : (
        <>
          <ul className="grid grid-cols-3 gap-2">
            {screenshots.map((shot, tile) => (
              <li key={shot.id}>
                <button
                  type="button"
                  onClick={() => viewer.open(screenshots, tile)}
                  className="block w-full cursor-pointer"
                >
                  <img
                    src={`/api/screenshots/${shot.id}/thumbnail`}
                    alt={`Screenshot from ${new Date(shot.captured_at).toLocaleString()}`}
                    loading="lazy"
                    decoding="async"
                    draggable={false}
                    className="aspect-square w-full rounded-md border object-cover"
                  />
                </button>
              </li>
            ))}
          </ul>
          {query.hasNextPage && (
            <Button
              variant="outline"
              size="sm"
              className="w-full"
              onClick={() => void query.fetchNextPage()}
              disabled={query.isFetchingNextPage}
            >
              Load more
            </Button>
          )}
        </>
      )}
      {viewer.viewer}
    </section>
  )
}
